import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

export type PlaylineDebugState = {
  sceneNumber: number;
  genTimes: number[];
  tokens: { input: number; output: number };
  model: string;
};

const DEFAULTS: PlaylineDebugState = {
  sceneNumber: 1,
  genTimes: [],
  tokens: { input: 0, output: 0 },
  model: "unknown",
};

/**
 * playline ごとの debug 用 state (= sceneNumber / genTimes / token usage / model) を
 * localStorage キー `palimia.debug.{playlineId}` に集約して読み書きする hook。
 *
 * 旧コード (PlayerScreen inline) は同一 key を 3 回 read していたが、 mount 時に 1 度
 * 読み込み、 state 変更ごとに 1 度 write する。 JSON parse 失敗時は DEFAULTS にフォールバック。
 *
 * 旧コードの useEffect deps から model が欠落していた潜在 bug (= model のみの変更が
 * 永続化されない) を解消する。
 */
export function usePlaylineDebugState(
  playlineId: string,
): [PlaylineDebugState, Dispatch<SetStateAction<PlaylineDebugState>>] {
  const key = `palimia.debug.${playlineId}`;
  const [state, setState] = useState<PlaylineDebugState>(() =>
    readPlaylineDebugState(key),
  );

  useEffect(() => {
    writePlaylineDebugState(key, state);
  }, [key, state]);

  return [state, setState];
}

function readPlaylineDebugState(key: string): PlaylineDebugState {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PlaylineDebugState>;
      return {
        sceneNumber: parsed.sceneNumber ?? DEFAULTS.sceneNumber,
        genTimes: parsed.genTimes ?? DEFAULTS.genTimes,
        tokens: {
          input: parsed.tokens?.input ?? DEFAULTS.tokens.input,
          output: parsed.tokens?.output ?? DEFAULTS.tokens.output,
        },
        model: parsed.model ?? DEFAULTS.model,
      };
    }
  } catch {
    /* localStorage 利用不可 / JSON parse 失敗時は DEFAULTS にフォールバック */
  }
  return DEFAULTS;
}

function writePlaylineDebugState(key: string, value: PlaylineDebugState): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* localStorage 利用不可時は無視 (= reader 体験を阻害しない) */
  }
}
